import { useRef, useEffect } from 'react';
import MonacoEditor, { OnMount, BeforeMount } from '@monaco-editor/react';

type Props = {
  value: string;
  onChange?: (v: string) => void;
  language?: string;
  readOnly?: boolean;
  minHeight?: number;
  maxHeight?: number;
  beforeMount?: BeforeMount;
  onCmdEnter?: () => void;
};

export default function AutoResizeMonaco({
                                           value,
                                           onChange,
                                           language = 'json',
                                           readOnly = false,
                                           minHeight = 500,
                                           maxHeight = 999999,
                                           beforeMount,
                                           onCmdEnter,
                                         }: Props) {
  const editorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current) {
      const contentHeight = editorRef.current.getContentHeight();
      const nextHeight = Math.max(minHeight, Math.min(contentHeight + 36, maxHeight));
      editorRef.current.layout({ width: editorRef.current.getDomNode()?.clientWidth || 0, height: nextHeight });
      if (containerRef.current) {
        containerRef.current.style.height = `${nextHeight}px`;
      }
    }
  }, [value, minHeight, maxHeight]);

  const onMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;

    const relayout = () => {
      try {
        const contentHeight = editor.getContentHeight();
        const nextHeight = Math.max(minHeight, Math.min(contentHeight + 36, maxHeight));
        editor.layout({ width: editor.getDomNode()?.clientWidth || 0, height: nextHeight });
        if (containerRef.current) {
          containerRef.current.style.height = `${nextHeight}px`;
        }
      } catch {}
    };

    relayout();
    editor.onDidContentSizeChange(relayout);

    if (onCmdEnter) {
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => onCmdEnter());
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        border: '1px solid #eee',
        borderRadius: 6,
        minHeight,
        maxHeight,
        overflow: 'hidden',
        background: readOnly ? '#fafafa' : '#fff',
      }}
    >
      <MonacoEditor
        value={value}
        language={language}
        onChange={(v) => onChange?.(v ?? '')}
        options={{
          readOnly,
          minimap: { enabled: false },
          fontSize: 14,
          wordWrap: 'on',
          automaticLayout: true,
          scrollBeyondLastLine: false,
          scrollbar: {
            vertical: 'hidden',
            horizontal: 'hidden',
            alwaysConsumeMouseWheel: false,
          },
          overviewRulerLanes: 0,
        }}
        onMount={onMount}
        beforeMount={beforeMount}
      />
    </div>
  );
}